import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { env } from '@/config/env';
import type { ResponseArray, ResponseObject } from '@/types/shared';
import type {
  AffiliateClick,
  AffiliateConversion,
  AffiliateCommission,
  AffiliatePayout,
} from '@/api/affiliate';

/**
 * Account module data — the signed-in employee's own profile, VCard and the
 * regulations they've accepted. Mirrors the datacentral-client-v4 contract
 * (`modules/Account/components/services`, `modules/VCard/services`,
 * `modules/Regulations/services`) so the same REAL endpoints back the rebuild.
 * On-call / day-off / business / workday "my" lists live in the sibling
 * `account-oncall.ts` and `account-lists.ts`.
 */

/* ── Current employee (profile) ────────────────────────────────────────── */
/** The full employee record for the signed-in user (`/employees/current`). */
export interface Employee {
  employee_id: number;
  employee_name: string;
  employee_code: string;
  employee_email: string;
  employee_email_company: string;
  employee_phone_number?: string;
  employee_birthday?: string;
  employee_gender: string;
  employee_marriage: string;
  employee_idno: string;
  employee_idno_issued_on?: string;
  employee_idno_issued_by?: string;
  employee_nation?: string;
  employee_nationality?: string;
  employee_religion?: string;
  employee_street?: string;
  employee_city?: string;
  employee_district?: string;
  employee_ward?: string;
  employee_temporary_street?: string;
  employee_contract_type: string;
  employee_seniority_level?: string;
  employee_start_date: string;
  employee_end_date?: string;
  employee_day_off_left: number;
  department_id: number;
  department_name: string;
  position_id: number;
  position_name: string;
}

export const accountKeys = {
  currentEmployee: ['account', 'employee', 'current'] as const,
  vcard: ['account', 'vcard', 'current'] as const,
  acceptedRegulations: ['account', 'regulations', 'my-accepted'] as const,
  referralStats: ['account', 'referral', 'stats'] as const,
  referralCustomers: (params: { page: number; page_size: number }) =>
    ['account', 'referral', 'customers', params] as const,
  referralClicks: (params: { limit: number; offset: number }) =>
    ['account', 'referral', 'clicks', params] as const,
  referralConversions: (params: { limit: number; offset: number }) =>
    ['account', 'referral', 'conversions', params] as const,
  referralCommissions: (params: { limit: number; offset: number }) =>
    ['account', 'referral', 'commissions', params] as const,
  referralPayouts: (params: { limit: number; offset: number }) =>
    ['account', 'referral', 'payouts', params] as const,
};

export function useCurrentEmployee() {
  return useQuery({
    queryKey: accountKeys.currentEmployee,
    queryFn: async (): Promise<Employee> => {
      const res = await dataCentralApi.get<ResponseObject<Employee>>('/employees/current');
      return res.data.result;
    },
    staleTime: 5 * 60_000,
  });
}

/** Editable contact/address payload for `PUT /employees/current`. */
export interface UpdateCurrentEmployeePayload {
  employee_phone_number: string;
  employee_email: string;
  employee_street: string;
  employee_city: string;
  employee_district: string;
  employee_ward: string;
  employee_temporary_street: string;
  employee_temporary_city: string;
  employee_temporary_district: string;
  employee_temporary_ward: string;
  // Required fields the API validates even when unchanged
  employee_name: string;
  employee_gender: string;
  employee_birthday: string;
  employee_marriage: string;
  employee_idno: string;
  employee_idno_issued_by: string;
  employee_idno_issued_on: string;
}

/** `PUT /employees/current` — self-service update of contact/address fields. */
export function useUpdateCurrentEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateCurrentEmployeePayload): Promise<Employee> => {
      const res = await dataCentralApi.put<ResponseObject<Employee>>(
        '/employees/current',
        payload,
      );
      return res.data.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.currentEmployee });
    },
  });
}

/* ── My VCard ──────────────────────────────────────────────────────────── */
export interface VCardSocial {
  key?: string;
  platform?: string;
  value?: string;
  url?: string;
}

/**
 * Digital contact card for the current employee (`/employees/vcards/current`).
 * Field names match the datacentral-client-v4 VCard contract exactly.
 */
export interface VCard {
  id: number;
  uuid: string;
  name: string;
  position: string;
  description: string;
  email: string;
  phone: string;
  address: string;
  avatar: string;
  socials?: VCardSocial[];
}

export function useMyVCard() {
  return useQuery({
    queryKey: accountKeys.vcard,
    queryFn: async (): Promise<VCard | null> => {
      const res = await dataCentralApi.get<ResponseObject<VCard>>('/employees/vcards/current');
      return res.data.result ?? null;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** All VCards owned by the current employee (`GET /employees/vcards`). */
export function useMyVCardList() {
  return useQuery({
    queryKey: ['account', 'vcards', 'list'] as const,
    queryFn: async (): Promise<VCard[]> => {
      const res = await dataCentralApi.get<ResponseArray<VCard>>('/employees/vcards?page=1&page_size=50');
      const d = res.data as ResponseArray<VCard> & { result?: VCard[] };
      return d.results ?? d.result ?? [];
    },
  });
}

/** `GET /employees/vcards/uuid/:uuid` — public lookup by UUID (no auth required for the card, but we proxy through the API here). */
export function useVCardByUuid(uuid: string | undefined) {
  return useQuery({
    queryKey: ['account', 'vcard', 'uuid', uuid] as const,
    enabled: !!uuid,
    retry: false,
    queryFn: async (): Promise<VCard> => {
      const res = await dataCentralApi.get<ResponseObject<VCard>>(`/employees/vcards/uuid/${uuid}`);
      return res.data.result;
    },
  });
}

export interface VCardSocialInput {
  key: string;
  value: string;
}

/** Editable VCard fields — matches the datacentral-client create/update payload. */
export interface VCardInput {
  name: string;
  position: string;
  description: string;
  email: string;
  phone: string;
  address: string;
  avatar?: string;
  socials?: VCardSocialInput[];
}

/** Platforms selectable for a VCard social link (matches legacy). */
export const VCARD_SOCIAL_PLATFORMS = [
  'facebook', 'twitter', 'instagram', 'linkedin', 'tiktok', 'youtube',
  'pinterest', 'reddit', 'discord', 'telegram', 'whatsapp',
] as const;

/**
 * Upload a VCard avatar via the presigned flow (matches legacy): ask for a
 * presigned PUT URL, upload the file to it, then return the final storage path
 * to store on the VCard.
 */
export async function uploadVCardAvatar(file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'png';
  const key = `vcard_avatar_${Date.now()}.${ext}`;
  const res = await dataCentralApi.post<ResponseObject<{ url: string }>>('/employees/vcards/presigned_url', { key });
  const url = res.data.result?.url;
  if (!url) throw new Error('No presigned URL');
  const put = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
  if (!put.ok) throw new Error('Avatar upload failed');
  return `${env.storage.baseUrl}/avatars/${key}`;
}

export async function createVCard(input: VCardInput) {
  return (await dataCentralApi.post('/employees/vcards', input)).data;
}
export async function updateVCard(id: number, input: VCardInput) {
  return (await dataCentralApi.put(`/employees/vcards/${id}`, input)).data;
}
export async function deleteVCard(id: number) {
  return (await dataCentralApi.delete(`/employees/vcards/${id}`)).data;
}

/* ── Accepted regulations ──────────────────────────────────────────────── */
export interface RegulationAttachment {
  attachment_file_type: string;
  attachment_file_name: string;
  /** Storage path — exchanged for a presigned URL via `getDownloadUrl`. */
  attachment_file_url: string;
}

/**
 * Exchange a storage path for a temporary presigned download URL
 * (`GET /drive/download?path=`). Used to view/download regulation attachments —
 * the file exists behind object storage, so a direct URL isn't in the payload.
 */
export async function getDownloadUrl(path: string): Promise<string | null> {
  const res = await dataCentralApi.get<ResponseObject<string>>('/drive/download', { params: { path } });
  return res.data.result ?? null;
}

/** A regulation/policy the current employee has accepted. */
export interface MyAcceptedRegulation {
  acceptance_id: number;
  regulation_id: number;
  regulation_title: string;
  regulation_summary: string;
  regulation_version: string;
  regulation_type: string;
  is_active: boolean;
  is_mandatory: boolean;
  accepted_at: string | null;
  acceptance_created_at: string | null;
  acceptance_updated_at: string | null;
  updated_at: string;
  attachments?: RegulationAttachment[];
}

export function useMyAcceptedRegulations() {
  return useQuery({
    queryKey: accountKeys.acceptedRegulations,
    queryFn: async (): Promise<MyAcceptedRegulation[]> => {
      const res = await dataCentralApi.get<ResponseArray<MyAcceptedRegulation>>(
        '/regulations/config/my-accepted',
      );
      const data = res.data as ResponseArray<MyAcceptedRegulation> & {
        result?: MyAcceptedRegulation[];
      };
      return data.results ?? data.result ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

/* ── My referral (employee affiliate) ──────────────────────────────────── */

/**
 * The signed-in employee's own referral dashboard — link, live stats and the
 * customers they referred. Mirrors legacy `modules/Account` services
 * (`/employees/current/referral-*`). Commission fields only render when
 * `earns_commission` is true (backend decides per policy audience).
 */
export interface EmployeeReferralSummary {
  referral_code: string;
  referral_link: string;
  total_clicks: number;
  total_conversions: number;
  referred_customers: number;
  conversion_rate: number;
  confirmed_commission: number;
  pending_commission: number;
  earns_commission: boolean;
}

export interface ReferralCustomer {
  customer_id: number;
  customer_name: string;
  customer_email: string;
  company_name?: string;
}

export function useMyReferralStats() {
  return useQuery({
    queryKey: accountKeys.referralStats,
    queryFn: async (): Promise<EmployeeReferralSummary> => {
      const res = await dataCentralApi.get<ResponseObject<EmployeeReferralSummary>>(
        '/employees/current/referral-stats',
      );
      return res.data.result;
    },
  });
}

export function useMyReferralCustomers(params: { page: number; page_size: number }) {
  return useQuery({
    queryKey: accountKeys.referralCustomers(params),
    queryFn: async (): Promise<ResponseArray<ReferralCustomer>> => {
      const res = await dataCentralApi.get<ResponseArray<ReferralCustomer>>(
        `/employees/current/referral-customers?page=${params.page}&page_size=${params.page_size}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

/** `GET /employees/current/referral-clicks` — the signed-in employee's click log (limit/offset). */
export function useMyReferralClicks(params: { limit: number; offset: number }) {
  return useQuery({
    queryKey: accountKeys.referralClicks(params),
    queryFn: async (): Promise<ResponseArray<AffiliateClick>> => {
      const res = await dataCentralApi.get<ResponseArray<AffiliateClick>>(
        `/employees/current/referral-clicks?${qs.stringify(params)}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

/** `GET /employees/current/referral-conversions` — the signed-in employee's conversion list (limit/offset). */
export function useMyReferralConversions(params: { limit: number; offset: number }) {
  return useQuery({
    queryKey: accountKeys.referralConversions(params),
    queryFn: async (): Promise<ResponseArray<AffiliateConversion>> => {
      const res = await dataCentralApi.get<ResponseArray<AffiliateConversion>>(
        `/employees/current/referral-conversions?${qs.stringify(params)}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

/** `GET /employees/current/referral-commissions` — the signed-in employee's commission audit trail (limit/offset). */
export function useMyReferralCommissions(params: { limit: number; offset: number }) {
  return useQuery({
    queryKey: accountKeys.referralCommissions(params),
    queryFn: async (): Promise<ResponseArray<AffiliateCommission>> => {
      const res = await dataCentralApi.get<ResponseArray<AffiliateCommission>>(
        `/employees/current/referral-commissions?${qs.stringify(params)}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

/** `GET /employees/current/referral-payouts` — the signed-in employee's payout list (limit/offset). */
export function useMyReferralPayouts(params: { limit: number; offset: number }) {
  return useQuery({
    queryKey: accountKeys.referralPayouts(params),
    queryFn: async (): Promise<ResponseArray<AffiliatePayout>> => {
      const res = await dataCentralApi.get<ResponseArray<AffiliatePayout>>(
        `/employees/current/referral-payouts?${qs.stringify(params)}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}
